// src/utils/reporteCotizacionGenerator.js
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { obtenerClientePorId } from '@/api/clientes.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
// 👈 NUEVO: Importar función que obtiene los datos de la empresa actual
import { obtenerDatosEmpresa } from './tenantConfig';

// Configuración por defecto (fallback)
const SYSTEM_JJ_CONFIG = {
  nombre: 'SYSTEM JJ, C.A.',
  rif: 'J-41023119-0',
  slogan: 'Soluciones Tecnológicas',
  direccion: 'CALLE 30, CON AV. 33, C.C LATIN CENTER, LOCAL 01',
  ciudad: 'ACARIGUA, EDO. PORTUGUESA',
  telefono: '04145146450 / 5516151 / 02556223297',
  email: 'SOPORTE@SYSTEMJJ.COM',
  banco: 'BANCO MERCANTIL',
  cuenta: '0105 0115 6711 1523 2630'
};

/**
 * 👈 NUEVO: Obtiene la config de la empresa actual con fallback
 */
const getEmpresa = () => {
  try {
    const datos = obtenerDatosEmpresa();
    return {
      nombre: datos.nombreComercial || datos.nombre || SYSTEM_JJ_CONFIG.nombre,
      rif: datos.rif || SYSTEM_JJ_CONFIG.rif,
      slogan: datos.slogan || SYSTEM_JJ_CONFIG.slogan,
      direccion: datos.direccion || SYSTEM_JJ_CONFIG.direccion,
      ciudad: datos.ciudad || SYSTEM_JJ_CONFIG.ciudad,
      telefono: datos.telefono1
        ? `${datos.telefono1}${datos.telefono2 ? ' / ' + datos.telefono2 : ''}`
        : SYSTEM_JJ_CONFIG.telefono,
      whatsapp: datos.whatsapp || datos.telefono1 || '',
      email: datos.email || SYSTEM_JJ_CONFIG.email,
      banco: datos.banco || SYSTEM_JJ_CONFIG.banco,
      cuenta: datos.cuenta || SYSTEM_JJ_CONFIG.cuenta,
      logoBase64: datos.logoBase64 || null,
    };
  } catch (err) {
    console.warn('⚠️ No se pudo obtener config del tenant, usando fallback:', err);
    return { ...SYSTEM_JJ_CONFIG, logoBase64: null };
  }
};

let LOGO_BASE64 = '';
let TASA_ACTUAL = 0;

/**
 * 👈 MODIFICADO: Obtiene el logo del tenant primero, y si no, carga el por defecto
 */
export const cargarLogo = async () => {
  try {
    // 👈 Primero intentar con el logo del tenant
    const empresa = getEmpresa();
    if (empresa.logoBase64) {
      // Si ya viene con prefijo data:, extraer solo el base64
      const base64Limpio = empresa.logoBase64.includes(',')
        ? empresa.logoBase64.split(',')[1]
        : empresa.logoBase64;
      LOGO_BASE64 = base64Limpio;
      console.log('✅ Logo del tenant cargado');
      return LOGO_BASE64;
    }

    // Fallback al logo por defecto
    const response = await fetch('/jj.png');
    if (!response.ok) throw new Error('Logo no encontrado');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        LOGO_BASE64 = reader.result.split(',')[1];
        resolve(LOGO_BASE64);
      };
      reader.onerror = () => {
        LOGO_BASE64 = '';
        resolve('');
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error cargando el logo:', error);
    LOGO_BASE64 = '';
    return '';
  }
};

export const getLogoBase64 = () => LOGO_BASE64;

export const obtenerTasaActual = async () => {
  try {
    const result = await obtenerTasaVentaActual();
    if (result.success && result.data) {
      TASA_ACTUAL = result.data.precio_usd_bs || result.data.precioUsdBs || 52.00;
      console.log('💰 Tasa actual cargada:', TASA_ACTUAL);
    } else {
      TASA_ACTUAL = 52.00;
    }
    return TASA_ACTUAL;
  } catch (error) {
    console.error('Error obteniendo tasa:', error);
    TASA_ACTUAL = 52.00;
    return TASA_ACTUAL;
  }
};

// ==================== UTILIDADES ====================
export const formatearFechaCorta = (fechaStr) => {
  if (!fechaStr) return '—';
  try {
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return fechaStr;
  }
};

export const formatearNumero = (valor, decimales = 2) => {
  if (valor === null || valor === undefined || isNaN(valor)) return '0,00';
  return Number(valor).toFixed(decimales).replace('.', ',');
};

export const formatearMonedaUSD = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return '$ 0,00';
  return `$ ${formatearNumero(valor)}`;
};

export const formatearMonedaBs = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return 'Bs 0,00';
  return `Bs ${formatearNumero(valor)}`;
};

export const convertirUsdABs = (usd, tasa) => {
  if (!usd || !tasa) return 0;
  return usd * tasa;
};

// ==================== OBTENER DATOS CLIENTE ====================
export const obtenerDatosClienteCompletos = async (cotizacion) => {
  const idCliente = cotizacion.idCliente || cotizacion.clienteId || cotizacion.IdCliente;
  
  let clienteData = null;
  
  if (idCliente) {
    try {
      console.log(`🔍 Buscando cliente ID: ${idCliente} para el PDF...`);
      const result = await obtenerClientePorId(idCliente);
      if (result.success && result.data) {
        clienteData = result.data;
        console.log('✅ Cliente encontrado:', clienteData);
      }
    } catch (error) {
      console.warn('⚠️ No se pudo obtener el cliente por ID:', error);
    }
  }
  
  if (clienteData) {
    return {
      nombre: clienteData.nombre + ' ' + (clienteData.apellido || ''),
      documento: clienteData.documento || '',
      direccion: clienteData.direccion || '',
      telefono: clienteData.telefono || '',
      email: clienteData.email || ''
    };
  }
  
  const cliente = cotizacion.cliente || cotizacion.Cliente || {};
  
  return {
    nombre: cotizacion.clienteNombre || 
            cotizacion.ClienteNombre || 
            cliente.nombre || 
            cliente.Nombre || 
            'CONSUMIDOR FINAL',
    
    documento: cotizacion.clienteDocumento || 
               cotizacion.ClienteDocumento || 
               cliente.documento || 
               cliente.Documento || 
               '',
    
    direccion: cotizacion.clienteDireccion || 
               cotizacion.ClienteDireccion || 
               cotizacion.direccion || 
               cotizacion.Direccion ||
               cliente.direccion || 
               cliente.Direccion || 
               cliente.domicilio ||
               cliente.Domicilio || 
               '',
    
    telefono: cotizacion.clienteTelefono || 
              cotizacion.ClienteTelefono || 
              cotizacion.telefono || 
              cotizacion.Telefono ||
              cliente.telefono || 
              cliente.Telefono || 
              ''
  };
};

// ==================== GENERAR HTML COTIZACIÓN ====================
export const generarHTMLCotizacionPDF = (cotizacion, datosCliente, tasa) => {
  if (!cotizacion) {
    return `<div style="padding:40px;text-align:center;font-family:Arial;">Cotización no encontrada</div>`;
  }

  const empresa = getEmpresa();   // 👈 NUEVO: datos dinámicos del tenant

  const totalUsd = Number(cotizacion.totalUsd) || 0;
  const subtotalUsd = Number(cotizacion.subtotalUsd) || 0;
  const ivaUsd = Number(cotizacion.ivaUsd) || 0;
  const descuentoUsd = Number(cotizacion.descuentoUsd) || 0;
  const ivaPorcentaje = cotizacion.ivaPorcentaje || 16;
  const detalles = cotizacion.detalles || [];
  
  // Convertir a Bolívares
  const tasaUsd = tasa || 52.00;
  const totalBs = convertirUsdABs(totalUsd, tasaUsd);
  const subtotalBs = convertirUsdABs(subtotalUsd, tasaUsd);
  const ivaBs = convertirUsdABs(ivaUsd, tasaUsd);
  const descuentoBs = convertirUsdABs(descuentoUsd, tasaUsd);
  
  const cliente = datosCliente || {
    nombre: 'CONSUMIDOR FINAL',
    documento: '',
    direccion: '',
    telefono: ''
  };

  const logoImg = getLogoBase64() ? `data:image/jpeg;base64,${getLogoBase64()}` : '';

  return `
    <div id="cotizacion-pdf" style="width:700px;margin:0 auto;padding:20px;font-family:'Courier New',monospace;font-size:11px;color:#000;background:white;">
      
      <!-- ======== HEADER - LOGO EN AMBAS ESQUINAS, INFO CENTRADA ======== -->
      <div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:10px;margin-bottom:8px;">
        <!-- Logo izquierda -->
        <div style="display:flex;align-items:center;">
          ${logoImg ? `<img src="${logoImg}" style="height:70px;width:auto;" onerror="this.style.display='none'">` : `<div style="width:70px;height:70px;"></div>`}
        </div>
        
        <!-- Información centrada -->
        <div style="text-align:center;flex:1;">
          <div style="font-size:16px;font-weight:bold;">${empresa.nombre}</div>
          <div style="font-size:10px;">${empresa.direccion}</div>
          <div style="font-size:10px;">${empresa.ciudad}</div>
          <div style="font-size:10px;">TELFS: ${empresa.telefono}</div>
          <div style="font-size:9px;color:#555;">${empresa.email}</div>
          <div style="font-size:10px;font-weight:bold;margin-top:2px;">RIF: ${empresa.rif}</div>
        </div>
        
        <!-- Logo derecha -->
        <div style="display:flex;align-items:center;">
          ${logoImg ? `<img src="${logoImg}" style="height:70px;width:auto;" onerror="this.style.display='none'">` : `<div style="width:70px;height:70px;"></div>`}
        </div>
      </div>

      <!-- ======== TÍTULO CON LÍNEA DEBAJO ======== -->
      <div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:4px;margin-bottom:6px;border-bottom:1px solid #ddd;">
        <div style="font-size:14px;font-weight:bold;">
          Cotizacion No. : ${cotizacion.numeroCotizacion || cotizacion.NumeroCotizacion || cotizacion.numero || '—'}
        </div>
        <div style="font-size:9px;">
          Fecha de Emision ${formatearFechaCorta(cotizacion.fechaEmision || cotizacion.FechaEmision || cotizacion.createdAt)} &nbsp;|&nbsp; Precios Validos Hasta : ${formatearFechaCorta(cotizacion.fechaValidez || cotizacion.FechaValidez)}
        </div>
      </div>

      <!-- ======== CLIENTE ======== -->
      <div style="padding-bottom:6px;margin-bottom:6px;">
        <div style="font-size:9px;line-height:1.4;">
          <div><span style="font-weight:bold;">Razon Social :</span> ${cliente.nombre}</div>
          <div><span style="font-weight:bold;">Domicilio :</span> ${cliente.direccion || '—'}</div>
          <div><span style="font-weight:bold;">Telefonos :</span> ${cliente.telefono || '—'}</div>
          <div><span style="font-weight:bold;">Rif y/o Cédula :</span> ${cliente.documento || '—'}</div>
        </div>
      </div>

      <!-- ======== TABLA ======== -->
      <div style="margin-bottom:6px;">
        <table style="width:100%;border-collapse:collapse;font-size:10px;">
          <thead>
            <tr style="border-bottom:1px solid #ddd;">
              <th style="padding:4px 8px;text-align:center;font-weight:bold;">CANT.</th>
              <th style="padding:4px 8px;text-align:left;font-weight:bold;">CODIGO</th>
              <th style="padding:4px 8px;text-align:left;font-weight:bold;">PRODUTO</th>
              <th style="padding:4px 8px;text-align:right;font-weight:bold;">COSTO UNIT.</th>
              <th style="padding:4px 8px;text-align:right;font-weight:bold;">TOTAL USD</th>
              <th style="padding:4px 8px;text-align:right;font-weight:bold;">TOTAL Bs</th>
            </tr>
          </thead>
          <tbody>
            ${detalles.length > 0 ? detalles.map((item, index) => {
              const codigo = item.codigo || item.productoCodigo || item.servicioCodigo || `ITEM-${index + 1}`;
              const descripcion = item.descripcionManual || item.productoNombre || item.servicioNombre || '—';
              const cantidad = Number(item.cantidad) || 1;
              const precio = Number(item.precioUnitarioUsd) || 0;
              const totalItem = Number(item.totalUsd) || (precio * cantidad);
              const totalItemBs = convertirUsdABs(totalItem, tasaUsd);
              return `
                <tr style="border-bottom:1px solid #eee;">
                  <td style="padding:4px 8px;text-align:center;">${cantidad}</td>
                  <td style="padding:4px 8px;">${codigo}</td>
                  <td style="padding:4px 8px;">${descripcion}</td>
                  <td style="padding:4px 8px;text-align:right;">${formatearMonedaUSD(precio)}</td>
                  <td style="padding:4px 8px;text-align:right;font-weight:bold;">${formatearMonedaUSD(totalItem)}</td>
                  <td style="padding:4px 8px;text-align:right;font-weight:bold;">${formatearMonedaBs(totalItemBs)}</td>
                </tr>
              `;
            }).join('') : `
              <tr>
                <td colspan="6" style="padding:20px;text-align:center;">No hay items registrados</td>
              </tr>
            `}
            <tr style="border-top:1px solid #ddd;border-bottom:1px solid #ddd;background:#f9f9f9;">
              <td colspan="4" style="padding:4px 8px;text-align:right;font-weight:bold;">TOTALES</td>
              <td style="padding:4px 8px;text-align:right;font-weight:bold;">${formatearMonedaUSD(totalUsd)}</td>
              <td style="padding:4px 8px;text-align:right;font-weight:bold;">${formatearMonedaBs(totalBs)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ======== TOTALES ======== -->
      <div style="padding:4px 0;margin-bottom:6px;max-width:400px;margin-left:auto;">
        <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;">
          <span>Sub-Total</span>
          <span style="font-weight:bold;">${formatearMonedaUSD(subtotalUsd)}</span>
          <span style="font-weight:bold;color:#666;">${formatearMonedaBs(subtotalBs)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;">
          <span>Dscto.</span>
          <span style="font-weight:bold;">${formatearMonedaUSD(descuentoUsd)}</span>
          <span style="font-weight:bold;color:#666;">${formatearMonedaBs(descuentoBs)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;">
          <span>IVA ${ivaPorcentaje}%</span>
          <span style="font-weight:bold;">${formatearMonedaUSD(ivaUsd)}</span>
          <span style="font-weight:bold;color:#666;">${formatearMonedaBs(ivaBs)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:4px 0 2px 0;border-top:1px solid #ddd;margin-top:2px;font-size:12px;font-weight:bold;">
          <span>Total</span>
          <span>${formatearMonedaUSD(totalUsd)}</span>
          <span style="color:#000;">${formatearMonedaBs(totalBs)}</span>
        </div>
        <div style="text-align:right;font-size:8px;color:#888;margin-top:2px;">
          Tasa: ${formatearNumero(tasaUsd)} Bs/USD
        </div>
      </div>

      <!-- ======== OBSERVACIONES Y FORMA DE PAGO ======== -->
      <div style="padding:4px 0;margin-bottom:6px;">
        <div style="font-size:9px;line-height:1.4;">
          <div><span style="font-weight:bold;">Observacion:</span></div>
          <div style="font-size:9px;margin-bottom:4px;">
            <span style="font-weight:bold;">FORMA DE PAGO:</span> Pago Móvil o Transferencia Bancaria A NOMBRE DE ${empresa.nombre} EN LA CTA. CORRIENTE. # ${empresa.cuenta} DEL ${empresa.banco}, RIF.: ${empresa.rif} ${empresa.email}
          </div>
          <div style="font-size:9px;margin-bottom:4px;">
            <span style="font-weight:bold;">CONDICIONES DE PAGO:</span> 75% al inicio del proyecto y 30% al finalizar la ejecución del mismo.
          </div>
          <div style="font-size:9px;">
            <span style="font-weight:bold;">POLÍTICAS:</span> Esta cotización tiene una validez de 30 días. Los precios están sujetos a cambios sin previo aviso. La instalación y configuración no incluyen licencias de software. Cualquier modificación al alcance del proyecto podrá generar costos adicionales.
          </div>
          ${cotizacion.observaciones || cotizacion.Observaciones ? `<div style="margin-top:4px;font-size:9px;"><span style="font-weight:bold;">NOTA ADICIONAL:</span> ${cotizacion.observaciones || cotizacion.Observaciones}</div>` : ''}
        </div>
      </div>

      <!-- ======== FIRMA ======== -->
      <div style="text-align:center;padding:8px 0 5px 0;margin-top:6px;">
        <div style="font-size:10px;font-weight:bold;">${empresa.nombre}</div>
        <div style="font-size:8px;color:#666;margin-top:2px;">________________________________</div>
        <div style="font-size:8px;color:#666;">Firma Autorizada</div>
      </div>

      <!-- ======== PIE ======== -->
      <div style="text-align:center;font-size:7px;color:#999;margin-top:6px;padding-top:4px;">
        ${empresa.nombre} - ${empresa.direccion} - ${empresa.ciudad}
      </div>
    </div>
  `;
};

// ==================== FUNCIÓN PRINCIPAL - DESCARGAR PDF ====================
export const descargarCotizacionPDF = async (cotizacion) => {
  if (!cotizacion) {
    alert('No hay cotización para generar PDF');
    return false;
  }

  try {
    // Cargar logo (del tenant o por defecto)
    LOGO_BASE64 = '';   // Resetear para que recargue el logo del tenant actual
    await cargarLogo();

    // Obtener tasa actual
    const tasa = await obtenerTasaActual();
    console.log('💰 Tasa para PDF:', tasa);

    const datosCliente = await obtenerDatosClienteCompletos(cotizacion);
    console.log('📋 Datos del cliente para PDF:', datosCliente);

    const numeroCotizacion = cotizacion.numeroCotizacion || cotizacion.NumeroCotizacion || cotizacion.numero || 'SIN_NUMERO';
    const nombreArchivo = `COTIZACION_${numeroCotizacion}.pdf`;

    const htmlContent = generarHTMLCotizacionPDF(cotizacion, datosCliente, tasa);

    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    container.style.cssText = 'position:absolute;left:-9999px;top:0;width:740px;background:white;padding:0;z-index:-1;';
    document.body.appendChild(container);

    await new Promise(resolve => setTimeout(resolve, 500));

    const canvas = await html2canvas(container, {
      scale: 2.5,
      useCORS: true,
      width: 740,
      height: container.scrollHeight,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = pdfHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
    heightLeft -= pdf.internal.pageSize.getHeight();

    while (heightLeft > 0) {
      position = heightLeft - pdfHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();
    }

    pdf.save(nombreArchivo);
    document.body.removeChild(container);

    return true;
  } catch (error) {
    console.error('Error generando PDF:', error);
    alert('Error al generar el PDF: ' + error.message);
    return false;
  }
};

// ==================== EXPORTACIÓN ====================
export default {
  SYSTEM_JJ_CONFIG,
  cargarLogo,
  getLogoBase64,
  obtenerTasaActual,
  formatearFechaCorta,
  formatearNumero,
  formatearMonedaUSD,
  formatearMonedaBs,
  convertirUsdABs,
  obtenerDatosClienteCompletos,
  generarHTMLCotizacionPDF,
  descargarCotizacionPDF
};